import { useEffect, useState } from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import api from '@/lib/api';

/**
 * TemplateSelector — selector reusable de plantillas WhatsApp APPROVED de un WABA.
 * BILINGÜE: Gupshup (configId → saved-templates) | Twilio (wabaId → templates/all).
 *
 * ⚠️ Devuelve la REFERENCIA ESTABLE del proveedor (Gupshup `template_id` | Twilio `content_sid`),
 * NUNCA el id local de Mongo. `wa_templates.id` ROTA en re-sync (delete+reinsert con UUID nuevo,
 * CLAUDE.md §4) — guardar el id local fue la causa del 4003 del piloto.
 *
 * Props:
 *   provider    — 'gupshup' (default) | 'twilio'.
 *   configId    — id del gupshup_waba_config (requerido si provider='gupshup').
 *   wabaId      — id de waba_accounts (requerido si provider='twilio').
 *   moduleType  — filtro de módulo (default "logicas"). Incluye plantillas SIN tag. null/'' = sin filtro.
 *   status      — filtro de estado (default "APPROVED").
 *   value       — stable_ref actualmente seleccionado (controlado).
 *   onSelect    — (payload) => void. payload = { provider, stable_ref, language, body, variables }.
 *                 `variables` viene en Twilio (lista de índices); Gupshup → [] (el grid usa el body).
 *   disabled    — deshabilita el control.
 */
export default function TemplateSelector({
  provider = 'gupshup',
  configId,
  wabaId,
  moduleType = 'logicas',
  status = 'APPROVED',
  value = '',
  onSelect,
  disabled = false,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // El id del WABA según el proveedor (gupshup_config_id | waba_accounts.id).
  const sourceId = provider === 'twilio' ? wabaId : configId;

  useEffect(() => {
    if (!sourceId) { setTemplates([]); return undefined; }
    let active = true;
    setLoading(true);

    // Rama de fetch por proveedor; normaliza cada item con `stable_ref` (la ref ESTABLE).
    const req = provider === 'twilio'
      ? api.get('/whatsapp/templates/all', { params: { provider: 'twilio', page_size: 100, waba_id: sourceId } })
          .then((res) => (Array.isArray(res.data?.items) ? res.data.items : [])
            // Molde NotificacionesTab: content_sid es la ref; cae a template_id/id si faltara.
            .map((t) => ({ ...t, stable_ref: t.content_sid || t.template_id || t.id })))
      : api.get(`/whatsapp/gupshup/saved-templates/${sourceId}`)
          .then((res) => (Array.isArray(res.data?.templates) ? res.data.templates : [])
            .map((t) => ({ ...t, stable_ref: t.template_id })));

    req
      .then((list) => {
        if (!active) return;
        let out = list;
        if (status) out = out.filter((t) => String(t.status || '').toUpperCase() === String(status).toUpperCase());
        // Solo plantillas etiquetadas para este módulo; red de seguridad: nunca esconder la
        // plantilla YA seleccionada en una receta existente (aunque sea sin-tag) → no se orfana al editar.
        if (moduleType) out = out.filter((t) => t.module_type === moduleType || t.stable_ref === value);
        // Sin ref ESTABLE usable no se puede referenciar la plantilla — se descarta.
        out = out.filter((t) => t.stable_ref);
        setTemplates(out);
      })
      .catch(() => { if (active) setTemplates([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [provider, sourceId, moduleType, status]);

  const handleChange = (stableRef) => {
    const tpl = templates.find((t) => t.stable_ref === stableRef);
    if (!tpl || !onSelect) return;
    onSelect({
      provider,
      stable_ref: tpl.stable_ref,                            // ← ref ESTABLE (template_id|content_sid), NO tpl.id local
      language: tpl.language || tpl.language_code || (provider === 'twilio' ? 'es_mx' : 'es_MX'),
      body: tpl.body || '',
      variables: Array.isArray(tpl.variables) ? tpl.variables : [],   // Twilio: índices; Gupshup: [] → grid usa el body
    });
  };

  const placeholder = !sourceId
    ? 'Selecciona primero un número WhatsApp'
    : loading
      ? 'Cargando plantillas…'
      : (templates.length ? 'Selecciona una plantilla' : 'Sin plantillas APPROVED para Lógicas');

  return (
    <Select
      value={value || ''}
      onValueChange={handleChange}
      disabled={disabled || !sourceId || loading}
    >
      <SelectTrigger className="bg-black/20 border-white/10 text-xs h-8" data-testid="template-selector">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {templates.map((tpl) => (
          <SelectItem key={tpl.stable_ref} value={tpl.stable_ref} className="text-xs">
            {tpl.name || tpl.element_name || tpl.friendly_name || tpl.stable_ref}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
